import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { resolvePostLogin, sanitizeReturnTo } from '../lib/returnTo';

/**
 * Lands here as the OAuth redirect_uri configured for the Hauraki Web IDE
 * client. react-oidc-context's AuthProvider auto-processes the `code`+`state`
 * params on the URL via UserManager.signinRedirectCallback() — this page
 * just shows a spinner while that happens, then routes the user on.
 *
 * Success -> the sanitized `returnTo` threaded through the OIDC round-trip,
 * or the portal home (NOT the legacy IDE dashboard). Error -> /login?error=...
 * Still-loading -> wait.
 */
const AuthCallback: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isLoading) return;

    if (auth.error) {
      const failure = auth.error as Error & { state?: { returnTo?: string }; userState?: { returnTo?: string } };
      const destination = sanitizeReturnTo(failure.userState?.returnTo ?? failure.state?.returnTo);
      // Never put raw provider error messages, authorization codes or state in
      // the URL. The invitation's HttpOnly context survives this failed login.
      if (destination === '/invitations/accept') navigate(destination, { replace: true, state: { signInFailed: true } });
      else navigate('/login?error=signin_failed', { replace: true });
      return;
    }

    if (auth.isAuthenticated) {
      const state = auth.user?.state as { returnTo?: string } | undefined;
      navigate(resolvePostLogin(state?.returnTo), { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.user, navigate]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Completing sign-in"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        background: '#1e1e1e',
        color: '#e0e0e0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: '2px solid #3a3a3a',
          borderTopColor: '#888',
          borderRadius: '50%',
          animation: 'auth-callback-spin 0.9s linear infinite',
        }}
      />
      <span>Completing sign-in…</span>
      <style>{`@keyframes auth-callback-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuthCallback;
